import type { SalesTransaction } from "../types/core";

// =============================================================================
// SELF-SET — SSOT for "did the closer source this call themselves?"
// =============================================================================
// DOCTRINE (Mako, 2026-05-18): "a self-set is essentially the post-call note
// has no setter attached." The closer picks "No Setter" on the post-call form →
// `trg_resolve_setter_attribution` forces `setter_id := NULL` and skips the
// lifetime pre-call lookup. So the SIGNAL IS THE NULL, not any flag.
//
// Self-set deals pay the closer `self_set_commission_base` (12%) instead of the
// standard `commission_rate` (10%) — see `utils/commission.ts` +
// `hooks/useInvoice.ts:93,114` (`is_self_set: !tx.setter_id`). This file must
// agree with those or the Transactions page and the invoice will disagree about
// which deals are self-set.
//
// ── THREE DEAD SIGNALS. Do NOT build against any of them: ────────────────────
//  1. `custom_data.is_self_set` — dead flag. Live census 2026-07-27 (Rayston,
//     90d): of 55 self-set rows, 53 are NULL and 2 are the STRING "false".
//     Nothing has ever written `true`. Reading it renders zero rows, silently.
//  2. `custom_data.setter_name` — unreliable. Same census: "No Setter" on 39
//     rows but NULL on the other 16 (rows predating the 2026-05-16 form change).
//     Filtering on it silently drops ~29% of self-set deals.
//  3. `event_type = 'self_set_claim'` — a DIFFERENT feature. That's the Lost
//     Leads / triage lead-CLAIM action (a closer claiming a dead lead to work
//     it), not a sale outcome. No commission code reads it.
//
// ── WHY THE event_type GATE IS LOAD-BEARING ──────────────────────────────────
// `setter_id IS NULL` is only meaningful on `sales_call_outcome`. Six other
// event types carry a NULL setter BY CONSTRUCTION (a webinar signup has no
// setter because signups don't have setters). Live counts, Rayston 90d:
//   webinar_signup 7,521 · typeform_submission 2,295 · self_set_claim 2,136 ·
//   call_booked 2,126 · call_cancelled 1,493 · low_ticket_purchase 881
// The Transactions page fetches `low_ticket_purchase` alongside
// `sales_call_outcome` (`usePaginatedTransactions.ts:317`), so an ungated
// predicate would tag all 881 low-ticket purchases as "self-set" on this exact
// page. The gate is not defensive — it is the difference between 55 and 936.
// =============================================================================

/** The event type on which a NULL setter actually means "self-set". */
export const SELF_SET_EVENT_TYPE = "sales_call_outcome";

/**
 * True when this row is a closer-sourced (self-set) sales call.
 *
 * Both conditions are required — see the event_type note above.
 */
export function isSelfSet(t: SalesTransaction): boolean {
  return (
    t.eventType === SELF_SET_EVENT_TYPE &&
    isSelfSetOutcome(t.setterId, t.ownershipLabel, t.filedAt ?? t.timestamp)
  );
}

// ── OWNERSHIP LABEL (Mako, 2026-10-06) ───────────────────────────────────────
// "No Setter is NOT a self-set." From 2026-10-01 (Singapore) the trigger writes
// custom_data.ownership_label and ONLY 'self_set' pays 12%: the closer's own claim
// before the booking + the closer's own live pre-call. A blank setter is
// 'no_setter' = the closer's normal 10%. Rows filed before Oct 1, or rows the
// backfill has not labelled yet, keep the old NULL-setter signal so nothing
// silently drops to 10% between the code deploy and the backfill.
// Trigger + backfill: sql/migrations/2026-10-06_setter_ownership_*.sql.
// Scope moved Sep 1 -> Oct 1 (Mako, 2026-10-15: "we're only going to fix everything in
// October, not September"). September keeps the old blank-setter signal.
export const OWNERSHIP_LABEL_FROM_MS = Date.parse("2026-10-01T00:00:00+08:00");

/** Self-set decision for one sales_call_outcome. Shared by the invoice and the Transactions page. */
export function isSelfSetOutcome(
  setterId: string | null | undefined,
  ownershipLabel: string | null | undefined,
  filedAt: string | null | undefined
): boolean {
  const ms = filedAt ? Date.parse(filedAt) : NaN;
  if (ownershipLabel && !Number.isNaN(ms) && ms >= OWNERSHIP_LABEL_FROM_MS) {
    return ownershipLabel === "self_set";
  }
  return !setterId;
}

/**
 * True when this row is a setter-sourced sales call (the 10% counterpart).
 *
 * NOT the negation of `isSelfSet` — non-call rows (low-ticket purchases,
 * signups) are neither. Use this when you need the explicit other side.
 */
export function isSetterSourced(t: SalesTransaction): boolean {
  return t.eventType === SELF_SET_EVENT_TYPE && !!t.setterId;
}

/** Count of self-set rows in a list — for badge/summary display. */
export function countSelfSet(transactions: SalesTransaction[]): number {
  let n = 0;
  for (const t of transactions) if (isSelfSet(t)) n++;
  return n;
}
